import React, { useState, useEffect, useMemo, useRef } from 'react';
import classes from './AddPackage.module.css';
import { StudioServices } from '../../pages/StudioProfile/StudioServices';
import CreativeModal from './CreativeModal';
import ServiceCard from './ServiceCard';
import { useBooking } from '../../store/bookingStore';
import { useScrollToTop } from '../../util/scrollToTop';

const AddPackage = ({ processedProfilePicture, 
                      firstName, 
                      userId, 
                      studioId, 
                      studioCreativesData, 
                      nextSlide 
                    }) => {
  const stepScrollRootRef = useRef(null);
  useScrollToTop({ delay: 50, rootRef: stepScrollRootRef });

  const { bookingData, updateBookingData } = useBooking();
  // Auto-check toggle if addFreelancer is true (user clicked "Mitbuchen" button)
  // But don't open modal if packages already exist (package was already added via "Mitbuchen")
  const hasExistingPackages = bookingData?.creativePackages && bookingData.creativePackages.length > 0;
  const [selectedOpenCreativeModal, setSelectedOpenCreativeModal] = useState(
    (bookingData?.addFreelancer && !hasExistingPackages) || false
  );
  const [selectedPackageForModal, setSelectedPackageForModal] = useState(null);
  const [openModalInDetailView, setOpenModalInDetailView] = useState(false);
  const [filteredStudioCreativesData, setFilteredStudioCreativesData] = useState(null);
  /** Matches StudioServices carousel index when choosing packages for the visible creative. */
  const [bookingCarouselIndex, setBookingCarouselIndex] = useState(0);

  const selectPackage = (pkg, creativeId) => {
    const existing = bookingData?.creativePackages?.find((p) => p.packageId === pkg.id);
    if (existing) {
      updateBookingData('creativePackages', bookingData?.creativePackages?.filter((p) => p.packageId !== pkg.id));
    } else {
      const bookedPackage = {
        creativeId: creativeId,
        packageId: pkg.id,
        // Persist packageType so the Phase 1/Phase 3 integrity signature stays stable
        // even if studioCreativesData is not yet loaded on a cold-cache Phase 3 resume
        // (mirrors CreativeModal.jsx; buildReservationFields falls back to this value).
        packageType: pkg.packageType || 'CUSTOM',
        duration: pkg.duration,
        price: pkg.price,
        specialRequests: '',
      };
      updateBookingData('creativePackages', [...(bookingData?.creativePackages || []), bookedPackage]);
    }
  };

  const removePackage = (packageId) => {
    updateBookingData('creativePackages', bookingData?.creativePackages?.filter((p) => p.packageId !== packageId));
  };

  const showSelectedPackages = useMemo(() => {
    if (!bookingData?.creativePackages || !studioCreativesData) return [];

    return bookingData.creativePackages
      .map((bookedPkg) => {
        // Find the creative in studioCreativesData
        const studioCreative = studioCreativesData.find(
          (sc) => sc.creative?.id === bookedPkg.creativeId
        );

        if (!studioCreative?.creative?.packages?.items) return null;

        // Find the package in the creative's packages
        const packageData = studioCreative.creative.packages.items.find(
          (pkg) => pkg.id === bookedPkg.packageId
        );

        if (!packageData) return null;

        return {
          ...packageData,
          creativeId: bookedPkg.creativeId,
          bookedPackageData: bookedPkg,
        };
      })
      .filter(Boolean);
  }, [bookingData?.creativePackages, studioCreativesData]);

  // Check if there are packages selected for the primary creative
  const hasSelectedPackages = useMemo(() => {
    if (!bookingData?.creativePackages || !studioCreativesData?.[0]?.creative?.id) return false;
    const primaryCreativeId = studioCreativesData[0].creative.id;
    return bookingData.creativePackages.some(
      (pkg) => pkg.creativeId === primaryCreativeId
    );
  }, [bookingData?.creativePackages, studioCreativesData]);

  // Sync selectedOpenCreativeModal with addFreelancer state
  // Only auto-open if addFreelancer is true AND no packages are already selected
  // This prevents the modal from opening when user clicks "Mitbuchen" and package is already added
  useEffect(() => {
    const hasPackages = bookingData?.creativePackages && bookingData.creativePackages.length > 0;
    if (bookingData?.addFreelancer && !selectedOpenCreativeModal && !hasPackages) {
      setSelectedOpenCreativeModal(true);
    }
  }, [bookingData?.addFreelancer, bookingData?.creativePackages]);

  const handleOpenPackage = (pkg) => {
    // Find the studioCreative data for this package and filter to only show that creative
    const studioCreative = studioCreativesData.find(
      (sc) => sc.creative?.id === pkg.creativeId
    );
    if (studioCreative) {
      setFilteredStudioCreativesData([studioCreative]);
      setSelectedPackageForModal(pkg);
      setOpenModalInDetailView(true);
      setSelectedOpenCreativeModal(true);
    }
  };

  const handleChangePackage = (pkg) => {
    // Find the studioCreative data for this package and filter to only show that creative
    const studioCreative = studioCreativesData.find(
      (sc) => sc.creative?.id === pkg.creativeId
    );
    if (studioCreative) {
      // Filter studioCreativesData to only include the relevant creative
      setFilteredStudioCreativesData([studioCreative]);
      setSelectedPackageForModal(null);
      setOpenModalInDetailView(false);
      setSelectedOpenCreativeModal(true);
    }
  };

  const handleCloseModal = () => {
    setSelectedOpenCreativeModal(false);
    setSelectedPackageForModal(null);
    setOpenModalInDetailView(false);
    setFilteredStudioCreativesData(null);
  };

  return (
    <div className={classes.container} ref={stepScrollRootRef}>
      <h1 className={`${classes.title} titleSemibold2`}>Kreative engagieren</h1>
      <div className={classes.studioServicesContainer}>
        <div className={classes.studioServicesWrapper}>
          <StudioServices 
            selectedOpenCreativeModal={selectedOpenCreativeModal} 
            setSelectedOpenCreativeModal={setSelectedOpenCreativeModal} 
            fromBooking={true}
            processedProfilePicture={processedProfilePicture}
            firstName={firstName}
            userId={userId}
            studioId={studioId}
            studioCreativesData={studioCreativesData}
            hasSelectedPackages={hasSelectedPackages}
            onBookingCarouselIndexChange={setBookingCarouselIndex}
          />
        </div>
        <div className={classes.selectedPackagesContainer}>
          {showSelectedPackages.map((pkg) => (
            <ServiceCard 
              key={pkg.id} 
              service={pkg} 
              selectPackage={selectPackage}
              creativeId={pkg.creativeId}
              isSelected={true}
              onOpenPackage={handleOpenPackage}
              onChangePackage={handleChangePackage}
              onRemovePackage={removePackage}
              fromAddPackage={true}
            />
          ))}
        </div>
      </div>
      {selectedOpenCreativeModal && (
        <CreativeModal 
          open={selectedOpenCreativeModal} 
          onClose={handleCloseModal} 
          studioCreativesData={filteredStudioCreativesData || studioCreativesData}
          initialPackage={selectedPackageForModal}
          openInDetailView={openModalInDetailView}
          activeCreativeIndex={filteredStudioCreativesData ? 0 : bookingCarouselIndex}
        />
      )}
      <div className={classes.bookingTotal}>
          <button className={classes.weiterButton} onClick={nextSlide}>Weiter</button>
      </div>
    </div>
  );
};

export default AddPackage;

